"use client";
import React, {
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
  useState,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { cn } from "utils";
import { usePopover } from "./usePopover";
import {
  getPopoverTransformOrigin,
  popoverSpringContentMotion,
} from "./popoverContentAnimation";
import { PopoverProps, PopoverRef } from "./types";

export const Popover = forwardRef<PopoverRef, PopoverProps>(
  (
    {
      children,
      content,
      disabled = false,
      className,
      contentClassName,
      delay = 150,
      placement = "bottom",
      trigger = "hover",
      closeOnFloatingLeave,
      onVisibilityChange,
      zIndex,
      floatingStyles,
      middleware,
      contentOffset,
      portalId,
      open,
      contentAnimation = "default",
    },
    ref,
  ) => {
    const {
      isOpen,
      refs,
      floatingStyles: initfloatingStyles,
      getReferenceProps,
      getFloatingProps,
      FloatingPortal,
      close,
    } = usePopover({
      disabled,
      delay,
      placement,
      trigger,
      onVisibilityChange,
      middleware,
      contentOffset,
      open,
    });

    const floatingProps = getFloatingProps({
      onMouseLeave: () => {
        if (closeOnFloatingLeave) {
          close();
        }
      },
    });

    useImperativeHandle(
      ref,
      () => ({
        close,
      }),
      [close],
    );

    // 定位层需在收起动效期间保持挂载；动效仅在内层，避免与 floating-ui 的 translate transform 冲突
    const [springLayerMounted, setSpringLayerMounted] = useState(
      () => contentAnimation === "spring" && isOpen,
    );
    useLayoutEffect(() => {
      if (contentAnimation !== "spring") {
        return;
      }
      if (isOpen) {
        setSpringLayerMounted(true);
      }
    }, [contentAnimation, isOpen]);

    return (
      <>
        <div
          ref={refs.setReference}
          className={cn("relative inline-block cursor-pointer", className)}
          {...getReferenceProps()}
        >
          {children}
        </div>

        {typeof window !== "undefined" && !disabled && (
          <FloatingPortal id={portalId}>
            {contentAnimation === "spring" ? (
              springLayerMounted ? (
                <div
                  ref={refs.setFloating}
                  style={{
                    ...initfloatingStyles,
                    zIndex: zIndex || 9999,
                    ...floatingStyles,
                  }}
                  {...floatingProps}
                >
                  <AnimatePresence
                    onExitComplete={() => {
                      setSpringLayerMounted(false);
                    }}
                  >
                    {isOpen ? (
                      <motion.div
                        className={cn("shadow-lg", contentClassName)}
                        style={{
                          transformOrigin: getPopoverTransformOrigin(placement),
                        }}
                        {...popoverSpringContentMotion}
                      >
                        {content}
                      </motion.div>
                    ) : null}
                  </AnimatePresence>
                </div>
              ) : null
            ) : isOpen ? (
              <div
                ref={refs.setFloating}
                style={{
                  ...initfloatingStyles,
                  zIndex: zIndex || 9999,
                  ...floatingStyles,
                }}
                {...floatingProps}
              >
                <div
                  className={cn(
                    "animate-in duration-200 fade-in-0 zoom-in-95",
                    "shadow-lg",
                    contentClassName,
                  )}
                >
                  {content}
                </div>
              </div>
            ) : null}
          </FloatingPortal>
        )}
      </>
    );
  },
);

Popover.displayName = "Popover";
